import {useState, type FocusEvent, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {Hint} from '@/components/ui/hint'
import {usePatchBateriaSolicitud, type BateriaSolicitudAdmin} from '@/hooks/bateria/use-bateria-solicitudes'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

export function BateriaDescartarDialog({target, onClose}: {target: BateriaSolicitudAdmin | null; onClose: () => void}) {
  const {t} = useTranslation()
  const patch = usePatchBateriaSolicitud()
  const [motivo, setMotivo] = useState('')
  const [motivoError, setMotivoError] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)

  function close() {
    setMotivo('')
    setMotivoError(undefined)
    setError(null)
    patch.reset()
    onClose()
  }

  function blurMotivo(e: FocusEvent<HTMLTextAreaElement>) {
    setMotivoError(validateRequired(e.target.value) ?? undefined)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const trimmed = motivo.trim()
    const err = validateRequired(trimmed)
    setMotivoError(err ?? undefined)
    if (err) return
    if (!target) return
    patch.mutate(
      {solicitudId: target.id, action: 'descartar', motivo: trimmed},
      {onSuccess: close, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && close()}>
      <DialogContent data-testid="bateria-descartar-dialog">
        <DialogHeader>
          <DialogTitle>{t('bateriaSolicitudes.admin.descartarTitle', {name: target?.business_name ?? ''})}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="bateria-descartar-motivo">{t('bateriaSolicitudes.admin.motivoLabel')}</Label>
            <Textarea
              id="bateria-descartar-motivo"
              data-testid="bateria-descartar-motivo"
              value={motivo}
              onChange={(e) => {
                setMotivo(e.target.value)
                setMotivoError(undefined)
                setError(null)
              }}
              onBlur={blurMotivo}
              maxLength={300}
              minRows={3}
              aria-invalid={!!motivoError}
            />
            <FieldError error={motivoError} />
            <Hint>{t('bateriaSolicitudes.admin.motivoHint')}</Hint>
          </div>
          <FormDialogFooter
            error={error}
            pending={patch.isPending}
            submitLabel={t('bateriaSolicitudes.admin.descartarSubmit')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
